import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Play, Clock, Users, Wrench, Camera } from "lucide-react";

interface BehindTheScenesVideo {
  id: string;
  title: string;
  description: string;
  category: 'installatie' | 'expertise' | 'werkwijze' | 'kwaliteit';
  duration: string;
  thumbnail: string;
  topic: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
}

const videos: BehindTheScenesVideo[] = [
  {
    id: "zonnepanelen-installatie",
    title: "Zonnepanelen installatie van A tot Z",
    description: "Volg ons team tijdens een complete zonnepanelen installatie. Van voorbereidingen tot ingebruikname.",
    category: "installatie",
    duration: "12:30",
    thumbnail: "/lovable-uploads/31f603c6-31a4-4504-a3b7-8026877acd02.png",
    topic: "Zonnepanelen",
    difficulty: "beginner"
  },
  {
    id: "warmtepomp-complexe-installatie",
    title: "Warmtepomp in monumentale woning",
    description: "Bijzondere uitdagingen bij het installeren van een warmtepomp in een beschermd monument.",
    category: "expertise",
    duration: "15:45",
    thumbnail: "/lovable-uploads/46dd609b-476b-4a82-a235-05cb878ddc9c.png",
    topic: "Warmtepomp",
    difficulty: "advanced"
  },
  {
    id: "kwaliteitscontrole",
    title: "Onze kwaliteitscontroles",
    description: "Zie hoe wij elke installatie controleren op veiligheid, kwaliteit en performance.",
    category: "kwaliteit",
    duration: "8:20",
    thumbnail: "/lovable-uploads/5589666c-2255-4f10-9a1a-3cf5d12dda77.png",
    topic: "Kwaliteitsborging",
    difficulty: "intermediate"
  },
  {
    id: "team-werkwijze",
    title: "Hoe ons team samenwerkt",
    description: "Een kijkje achter de schermen van onze professionele werkwijze en teamcommunicatie.",
    category: "werkwijze",
    duration: "6:15",
    thumbnail: "/lovable-uploads/7a2127f2-183e-4455-bbd8-8fbe4fcef477.png",
    topic: "Teamwork",
    difficulty: "beginner"
  },
  {
    id: "laadpaal-smart-installatie",
    title: "Smart laadpaal installatie uitgelegd",
    description: "Stap voor stap uitleg van een slimme laadpaal installatie met app-integratie.",
    category: "installatie",
    duration: "9:40",
    thumbnail: "/lovable-uploads/815db32a-8762-4c66-aa95-d00500f2f887.png",
    topic: "Laadpaal",
    difficulty: "intermediate"
  },
  {
    id: "batterij-integratie",
    title: "Thuisbatterij koppelen aan bestaande installatie",
    description: "Hoe we een thuisbatterij integreren met bestaande zonnepanelen en elektra-installatie.",
    category: "expertise",
    duration: "11:25",
    thumbnail: "/lovable-uploads/95d45f7a-42b7-4b2f-ab60-00ae1b2af357.png",
    topic: "Thuisbatterij",
    difficulty: "advanced"
  }
];

const categories = [
  { key: 'all', label: 'Alle video\'s', icon: <Camera className="w-4 h-4" /> },
  { key: 'installatie', label: 'Installaties', icon: <Wrench className="w-4 h-4" /> },
  { key: 'expertise', label: 'Expertise', icon: <Users className="w-4 h-4" /> },
  { key: 'werkwijze', label: 'Werkwijze', icon: <Clock className="w-4 h-4" /> },
  { key: 'kwaliteit', label: 'Kwaliteit', icon: <Play className="w-4 h-4" /> }
];

export function BehindTheScenes() {
  const [selectedCategory, setSelectedCategory] = React.useState<string>('all');
  
  const filteredVideos = selectedCategory === 'all' 
    ? videos 
    : videos.filter(v => v.category === selectedCategory);

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'advanced': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getDifficultyLabel = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'Basis';
      case 'intermediate': return 'Gemiddeld';
      case 'advanced': return 'Gevorderd';
      default: return difficulty;
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold">Behind the Scenes</h2>
        <p className="text-muted-foreground">
          Ontdek hoe wij werken: van voorbereiding tot voltooiing
        </p>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2 justify-center">
        {categories.map((category) => (
          <Button
            key={category.key}
            variant={selectedCategory === category.key ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedCategory(category.key)}
            className="flex items-center gap-2"
          >
            {category.icon}
            {category.label}
          </Button>
        ))}
      </div>

      {/* Video grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filteredVideos.map((video) => (
          <Card key={video.id} className="overflow-hidden group hover:shadow-lg transition-all duration-300">
            <div className="relative">
              <img 
                src={video.thumbnail} 
                alt={video.title}
                className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <Button size="lg" className="rounded-full w-16 h-16">
                  <Play className="w-6 h-6 ml-1" />
                </Button>
              </div>
              <div className="absolute top-2 right-2 flex gap-2">
                <Badge className="bg-black/70 text-white">
                  {video.duration}
                </Badge>
                <Badge className={getDifficultyColor(video.difficulty)}>
                  {getDifficultyLabel(video.difficulty)}
                </Badge>
              </div>
            </div>
            
            <CardHeader>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{video.topic}</Badge>
                  <Badge variant="outline" className="capitalize">
                    {video.category}
                  </Badge>
                </div>
                <CardTitle className="text-lg leading-tight">
                  {video.title}
                </CardTitle>
                <CardDescription className="text-sm">
                  {video.description}
                </CardDescription>
              </div>
            </CardHeader>
            
            <CardContent>
              <Button className="w-full group">
                <Play className="w-4 h-4 mr-2" />
                Bekijk Video
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Educational CTA */}
      <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="text-center py-8">
          <h3 className="text-xl font-bold mb-2">Transparantie in actie</h3>
          <p className="text-muted-foreground mb-4">
            Bij SOL-VI gelovet we in transparantie. Zie precies hoe wij werken en waarom kwaliteit onze prioriteit is.
          </p>
          <div className="flex gap-3 justify-center">
            <Button size="lg">
              Bekijk Alle Video's
            </Button>
            <Button variant="outline" size="lg">
              Plan Bezoek
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Educational note */}
      <Card className="border-l-4 border-l-primary">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Camera className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
            <div className="space-y-1">
              <h4 className="font-medium">Educatieve Content</h4>
              <p className="text-sm text-muted-foreground">
                Deze video's zijn bedoeld om u inzicht te geven in onze werkwijze en kwaliteitsstandaarden. 
                Voor technische vragen of specifieke installatie-eisen nemen we graag persoonlijk contact met u op.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}